import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import { ui } from '../components/adminUi';
import Icon from '../components/Icon';
import { CategoryIcon, ICON_OPTIONS, DEFAULT_ICON } from '../components/CategoryIcons';

export default function ServiceCategoriesPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // modal: null = closed | 'add' | category object = edit
  const [modal, setModal] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formIcon, setFormIcon] = useState(DEFAULT_ICON);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    setFetchError('');
    try {
      const res = await apiFetch('/api/admin/service-categories', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setFetchError("Kategoriyalarni yuklashda xatolik yuz berdi. Sahifani yangilang.");
    } finally {
      setPageLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const openAdd = () => {
    setFormName('');
    setFormDesc('');
    setFormIcon(DEFAULT_ICON);
    setFormError('');
    setModal('add');
  };

  const openEdit = (cat) => {
    setFormName(cat.name);
    setFormDesc(cat.description || '');
    setFormIcon(cat.iconName || DEFAULT_ICON);
    setFormError('');
    setModal(cat);
  };

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    setFormError('');
    if (!formName.trim()) {
      setFormError("Nom maydoni majburiy");
      return;
    }
    setSaving(true);
    try {
      const isEdit = modal !== 'add';
      const path = isEdit
        ? `/api/admin/service-categories/${modal.id}`
        : '/api/admin/service-categories';
      const res = await apiFetch(path, auth.token, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify({
          name: formName.trim(),
          description: formDesc.trim() || null,
          iconName: formIcon || DEFAULT_ICON,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFormError(d.message || 'Saqlashda xatolik yuz berdi');
        return;
      }
      closeModal();
      await loadCategories();
    } catch {
      setFormError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/service-categories/${deleteTarget.id}`, auth.token, {
        method: 'DELETE',
      });
      setDeleteTarget(null);
      await loadCategories();
    } catch {} finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>Xizmat kategoriyalari</h1>
          <p style={s.subtitle}>Barcha xizmat turlarini boshqaring</p>
        </div>
        <button style={s.addBtn} onClick={openAdd}>+ Qo'shish</button>
      </div>

      {fetchError && <div style={s.errorBanner}>{fetchError}</div>}

      {pageLoading ? (
        <div style={s.centerMsg}>Yuklanmoqda...</div>
      ) : categories.length === 0 && !fetchError ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>
            <Icon name="folder" size={48} color="var(--color-primary)" strokeWidth={1.75} />
          </div>
          <p style={s.emptyText}>Hali kategoriya yo'q</p>
          <p style={s.emptyHint}>Birinchi kategoriyani qo'shish uchun "+ Qo'shish" tugmasini bosing.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '48px' }}>#</th>
                <th style={{ ...s.th, width: '64px' }}>Ikon</th>
                <th style={s.th}>Nomi</th>
                <th style={s.th}>Tavsif</th>
                <th style={{ ...s.th, width: '220px', textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, i) => (
                <tr key={cat.id} style={s.tr}>
                  <td style={{ ...s.td, color: 'var(--color-text-muted)' }}>{i + 1}</td>
                  <td style={s.td}>
                    <span style={s.iconBadge}>
                      <CategoryIcon name={cat.iconName || DEFAULT_ICON} size={22} color="var(--color-text)" />
                    </span>
                  </td>
                  <td style={{ ...s.td, fontWeight: '600', color: 'var(--color-text)' }}>{cat.name}</td>
                  <td style={{ ...s.td, color: 'var(--color-text-muted)' }}>
                    {cat.description || <span style={{ fontStyle: 'italic', color: 'var(--color-border)' }}>—</span>}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <div style={s.actions}>
                      <button
                        style={s.propBtn}
                        onClick={() => navigate(`/admin/service-categories/${cat.id}/fields`)}
                      >
                        Xususiyatlar
                      </button>
                      <button style={s.editBtn} onClick={() => openEdit(cat)}>Tahrir</button>
                      <button style={s.delBtn} onClick={() => setDeleteTarget(cat)}>O'chirish</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {modal !== null && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>
              {modal === 'add' ? 'Yangi kategoriya' : 'Kategoriyani tahrirlash'}
            </h2>

            <div style={s.field}>
              <label style={s.label}>Nomi <span style={s.req}>*</span></label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                style={s.input}
                placeholder="Kategoriya nomini kiriting"
                autoFocus
                disabled={saving}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Tavsif</label>
              <textarea
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                style={s.textarea}
                placeholder="Ixtiyoriy tavsif"
                rows={3}
                disabled={saving}
              />
            </div>

            <div style={s.field}>
              <label style={s.label}>Ikon</label>
              <div style={s.iconGrid}>
                {ICON_OPTIONS.map((opt) => {
                  const selected = formIcon === opt.name;
                  return (
                    <button
                      type="button"
                      key={opt.name}
                      title={opt.label}
                      onClick={() => !saving && setFormIcon(opt.name)}
                      style={{ ...s.iconCell, ...(selected ? s.iconCellSelected : {}) }}
                      disabled={saving}
                    >
                      <CategoryIcon
                        name={opt.name}
                        size={24}
                        color="var(--color-text)"
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {formError && <div style={s.formError}>{formError}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeModal} disabled={saving}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...s.modalCard, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>O'chirishni tasdiqlang</h2>
            <p style={s.confirmText}>
              <strong>"{deleteTarget.name}"</strong> kategoriyasini o'chirishni xohlaysizmi?
              Bu amalni qaytarib bo'lmaydi.
            </p>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.deleteConfirmBtn, ...(deleting ? s.saveBtnDisabled : {}) }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "O'chirilmoqda..." : "O'chirish"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const s = {
  pageHeader: ui.pageHeader,
  title: ui.title,
  subtitle: ui.subtitle,
  addBtn: ui.addBtn,
  errorBanner: ui.errorBanner,
  centerMsg: ui.centerMsg,
  empty: ui.empty,
  emptyIcon: ui.emptyIcon,
  emptyText: ui.emptyText,
  emptyHint: ui.emptyHint,
  tableWrap: ui.tableWrap,
  table: ui.table,
  th: ui.th,
  tr: ui.tr,
  td: ui.td,
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  propBtn: { ...ui.btnSm, ...ui.btnBlue },
  editBtn: { ...ui.btnSm, ...ui.btnBlue },
  delBtn: { ...ui.btnSm, ...ui.btnRed },
  overlay: ui.overlay,
  modalCard: ui.modalCard,
  modalTitle: ui.modalTitle,
  field: ui.field,
  label: ui.label,
  req: ui.req,
  input: ui.input,
  textarea: ui.textarea,
  formError: ui.formError,
  modalActions: ui.modalActions,
  cancelBtn: ui.cancelBtn,
  saveBtn: ui.saveBtn,
  saveBtnDisabled: ui.btnDisabled,
  deleteConfirmBtn: {
    ...ui.saveBtn,
    background: 'var(--color-danger)',
    color: 'var(--color-on-dark)',
  },
  confirmText: {
    fontSize: '14px',
    color: 'var(--color-text)',
    lineHeight: '1.6',
    margin: '0 0 20px',
  },
  iconBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-btn)',
    background: 'var(--color-surface-alt)',
  },
  iconGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
    gap: '8px',
  },
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-btn)',
    cursor: 'pointer',
    padding: 0,
    transition: 'border-color 0.15s, background 0.15s',
  },
  iconCellSelected: {
    borderColor: 'var(--color-primary)',
    background: 'var(--color-primary)',
  }
};
